import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ScanLine, Mail, KeyRound, CheckCircle2, Copy as CopyIcon, ArrowLeft, Info, Eye, EyeOff } from 'lucide-react';import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';
import { Logo } from '../brand';

function Hero() {
    return (
        <div className="flex items-center gap-3 mb-6">
            <Logo variant="icon" size={52} data-testid="brand-logo-auth" />
            <div>
                <div className="font-heading text-2xl leading-tight">Ease My Bill</div>
                <div className="text-xs text-muted-foreground">Snap. Scan. Save.</div>
            </div>
        </div>
    );
}

function AuthShell({ children, testid }) {
    return (
        <div
            data-testid={testid}
            className="min-h-screen w-full flex items-center justify-center px-4 py-10 dashboard-hero app-noise"
        >
            <div className="w-full max-w-md">
                <Hero />
                {children}
            </div>
        </div>
    );
}

/**
 * "Continue with Google" button. Uses Emergent's hosted OAuth broker so
 * we don't have to manage a Google Client ID or Consent Screen.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function GoogleSignInButton({ label = 'Continue with Google', testid = 'google-signin-btn', size = 'md' }) {
    const onClick = () => {
        // Return the user back to their current app origin at /dashboard.
        // AppRouter will detect the #session_id= fragment and dispatch to
        // AuthCallback → exchange for JWT → land on /dashboard.
        const redirectUrl = window.location.origin + '/dashboard';
        const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        window.location.href = authUrl;
    };
    const heightClass = size === 'lg' ? 'h-12 text-base' : 'h-10 text-sm';
    const iconSize = size === 'lg' ? 22 : 18;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`w-full inline-flex items-center justify-center gap-3 ${heightClass} rounded-md border border-input bg-background font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
            aria-label={label}
        >
            <svg width={iconSize} height={iconSize} viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            {label}
        </button>
    );
}

/**
 * Small "or" divider between Google and email/password forms.
 */
function OrDivider() {
    return (
        <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
        </div>
    );
}

/* --------------------------------------------------------------
 * COMBINED LOGIN + REGISTER (first-page)
 * -------------------------------------------------------------- */
export function LoginPage() {
    const { initialized, loading, login, registerStart, registerResend, registerVerify, user } = useAuth();
    const [sp] = useSearchParams();
    const nav = useNavigate();

    // Tabs: 'login' | 'register'
    const [tab, setTab] = useState('login');

    // Shared form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Multi-tenant OTP signup state
    // 'form' = collect email+password, 'otp' = enter code, 'success' = show recovery code
    const [signupStep, setSignupStep] = useState('form');
    const [otp, setOtp] = useState('');
    const [otpTtl, setOtpTtl] = useState(0); // seconds remaining
    const [resendCooldown, setResendCooldown] = useState(0); // seconds

    // Register success (recovery code)
    const [recovery, setRecovery] = useState(null);
    const [trialDays, setTrialDays] = useState(30);

    // Flag: last login attempt failed because the account doesn't exist
    const [accountMissing, setAccountMissing] = useState(false);

    // Google-first UI: the email/password form is hidden by default. Users
    // reveal it via the "Trouble with Google?" text link. Kept as an escape
    // hatch for legacy accounts that predate the Google-only launch.
    const [showEmailFallback, setShowEmailFallback] = useState(false);

    // Pre-select the appropriate tab based on ?tab param
    useEffect(() => {
        const wantSignup = (sp.get('tab') || '').toLowerCase() === 'signup';
        if (wantSignup) setTab('register');
        else setTab('login');
    }, [sp]);

    // OTP TTL countdown timer
    useEffect(() => {
        if (otpTtl <= 0) return;
        const t = setInterval(() => setOtpTtl((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [otpTtl]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    if (loading)
        return (
            <AuthShell testid="auth-loading">
                <div className="text-sm text-muted-foreground">Loading…</div>
            </AuthShell>
        );

    // Already logged in → send to intended next
    if (user && !recovery) return <Navigate to={sp.get('next') || '/dashboard'} replace />;

    async function submitLogin(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success('Welcome back');
            nav(sp.get('next') || '/', { replace: true });
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 401) {
                toast.error('Invalid email or password');
            } else if (status === 403) {
                toast.error(detail || 'Please verify your email first');
            } else {
                toast.error(detail || 'Login failed');
            }
        } finally {
            setSubmitting(false);
        }
    }

    // ------------------------------------------------------------
    // Multi-tenant OTP signup: Step 1 → email+password → send OTP
    // ------------------------------------------------------------
    async function submitRegisterStart(e) {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        setSubmitting(true);
        try {
            const r = await registerStart(email.trim().toLowerCase(), password);
            if (r.already_registered) {
                toast.error('An account already exists for this email. Please log in instead.');
                setTab('login');
                return;
            }
            setSignupStep('otp');
            setOtpTtl(r.ttl_seconds || 600);
            setResendCooldown(45);
            toast.success('We sent you a verification code by email.');
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (Array.isArray(detail)) toast.error(detail[0]?.msg || 'Signup failed');
            else toast.error(detail || 'Signup failed');
        } finally {
            setSubmitting(false);
        }
    }

    // ------------------------------------------------------------
    // Multi-tenant OTP signup: Step 2 → verify code → account created
    // 2026-01: We used to show a "save this recovery code" screen right
    // after signup — that step has been removed. Password recovery now
    // uses the same email-OTP flow as signup verification (see
    // /forgot-password), so there's nothing for the user to memorise.
    // ------------------------------------------------------------
    async function submitRegisterVerify(e) {
        e.preventDefault();
        if (!otp || otp.length < 4) {
            toast.error('Please enter the code from your email');
            return;
        }
        setSubmitting(true);
        try {
            await registerVerify(email.trim().toLowerCase(), otp.trim());
            toast.success('Account created — 30-day free trial started');
            nav('/', { replace: true });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || 'Invalid code. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleResendOtp() {
        if (resendCooldown > 0) return;
        setSubmitting(true);
        try {
            const r = await registerResend(email.trim().toLowerCase());
            setOtpTtl(r.ttl_seconds || 600);
            setResendCooldown(45);
            toast.success('New code sent — check your email');
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || 'Could not resend code');
        } finally {
            setSubmitting(false);
        }
    }

    function handleChangeEmail() {
        // Return to the form step so the user can edit their email/password
        setSignupStep('form');
        setOtp('');
        setOtpTtl(0);
        setResendCooldown(0);
    }

    function copyRecovery() {
        if (!recovery) return;
        navigator.clipboard.writeText(recovery).then(
            () => toast.success('Recovery code copied'),
            () => toast.error('Could not copy — select it manually')
        );
    }

    // 2026-01: `recovery` will remain null on all new signups. The old
    // "Save your recovery code" screen has been removed from the primary
    // signup flow; the state and helpers stay in place only in case a
    // future legacy admin action ever needs to re-surface a code.

    return (
        <AuthShell testid="login-page">
            <Card className="p-6 border-border/80">
                <div className="text-center mb-6">
                    <h1 className="font-heading text-2xl mb-1">Welcome to Ease My Bill</h1>
                    <p className="text-sm text-muted-foreground">
                        Sign in or create your account with Google — one click, no passwords.
                    </p>
                </div>

                {/* Primary CTA — massive, front-and-center. This is now the
                    only surface most users will ever interact with. Google
                    handles both new signups and returning logins in a single
                    flow (see backend auth_google_session). */}
                <GoogleSignInButton
                    label="Continue with Google"
                    testid="google-signin-login-btn"
                    size="lg"
                />

                <p className="text-xs text-muted-foreground text-center mt-3">
                    New here? Google will create your account automatically —
                    you'll get a <strong>30-day free trial</strong>, no card needed.
                </p>

                {/* Legacy escape hatch — hidden by default. We're keeping the
                    email/password endpoints alive on the backend so any pre-
                    existing user who was signed up with a non-Google email
                    can still get in. Show/hide is client-side only. */}
                <div className="mt-6 pt-5 border-t border-border">
                    {!showEmailFallback ? (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowEmailFallback(true)}
                                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                                data-testid="show-email-login-toggle"
                            >
                                Trouble with Google? Sign in with email &amp; password
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Legacy sign-in
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowEmailFallback(false)}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="hide-email-login-toggle"
                                >
                                    Hide
                                </button>
                            </div>
                            <form onSubmit={submitLogin} className="space-y-3" data-testid="login-form">
                                <div>
                                    <Label>Email</Label>
                                    <Input
                                        data-testid="login-email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@company.com"
                                    />
                                </div>
                                <div>
                                    <Label>Password</Label>
                                    <PasswordInput
                                        data-testid="login-password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    variant="outline"
                                    className="w-full"
                                    disabled={submitting}
                                    data-testid="login-submit"
                                >
                                    {submitting ? 'Signing in…' : 'Log in with email'}
                                </Button>
                                <div className="text-center text-sm">
                                    <a
                                        href="/forgot-password"
                                        className="text-primary hover:underline"
                                        data-testid="login-forgot-link"
                                    >
                                        Forgot password?
                                    </a>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </Card>
            <p className="text-center text-xs text-muted-foreground mt-4">
                By continuing, you agree to our{' '}
                <a href="/terms" className="underline hover:text-foreground">Terms</a> and{' '}
                <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
            </p>
        </AuthShell>
    );
}

/* --------------------------------------------------------------
 * SetupPage — kept for backward compatibility, forwards to /login
 * -------------------------------------------------------------- */
export function SetupPage() {
    return <Navigate to="/login" replace />;
}

/* --------------------------------------------------------------
 * ForgotPasswordPage — 2-step email-OTP password reset (2026-01)
 *
 * Step 1: email → server sends 6-digit code
 * Step 2: code + new password → server sets password + auto-signs-in
 *
 * Uses the same OTP UX pattern as signup verification, so users don't have
 * to remember or store any recovery code.
 * -------------------------------------------------------------- */
export function ForgotPasswordPage() {
    const nav = useNavigate();
    const { setSession } = useAuth();
    const [step, setStep] = useState('email'); // 'email' | 'verify'
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [pw, setPw] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [otpTtl, setOtpTtl] = useState(0);
    const [resendCooldown, setResendCooldown] = useState(0);

    // TTL + resend countdown ticker
    useEffect(() => {
        if (otpTtl <= 0) return;
        const t = setInterval(() => setOtpTtl((v) => Math.max(0, v - 1)), 1000);
        return () => clearInterval(t);
    }, [otpTtl]);
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setInterval(() => setResendCooldown((v) => Math.max(0, v - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    async function submitEmail(e) {
        e.preventDefault();
        const e2 = email.trim().toLowerCase();
        if (!e2 || !e2.includes('@')) {
            toast.error('Enter a valid email');
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.forgotStart(e2);
            setOtpTtl(r?.ttl_seconds || 600);
            setResendCooldown(60);
            setStep('verify');
            toast.success('If that email is registered, we\'ve sent a 6-digit code.');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not send reset code');
        } finally {
            setSubmitting(false);
        }
    }

    async function submitVerify(e) {
        e.preventDefault();
        if (!otp || otp.length < 4) {
            toast.error('Enter the 6-digit code from your email');
            return;
        }
        if (!pw || pw.length < 8) {
            toast.error('New password must be at least 8 characters');
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.forgotVerify(email.trim().toLowerCase(), otp.trim(), pw);
            // The endpoint returns a fresh JWT — sign the user straight in.
            setSession({ token: r.token, user: r.user });
            toast.success('Password updated. You\'re signed in.');
            nav('/', { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Invalid code or password');
        } finally {
            setSubmitting(false);
        }
    }

    async function resendCode() {
        if (resendCooldown > 0) return;
        setSubmitting(true);
        try {
            const r = await api.forgotStart(email.trim().toLowerCase());
            setOtpTtl(r?.ttl_seconds || 600);
            setResendCooldown(60);
            toast.success('New code sent — check your email');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not resend code');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <AuthShell testid="forgot-page">
            <Card className="p-6 border-border/80">
                <div className="flex items-center gap-2 mb-4">
                    <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <Mail size={18} />
                    </div>
                    <div>
                        <h1 className="font-heading text-xl">Reset your password</h1>
                        <p className="text-xs text-muted-foreground">
                            {step === 'email'
                                ? 'We\'ll email you a 6-digit code to verify it\'s you.'
                                : `Code sent to ${email}. Enter it below with a new password.`}
                        </p>
                    </div>
                </div>

                {step === 'email' && (
                    <form onSubmit={submitEmail} className="space-y-4" data-testid="forgot-email-form">
                        <div>
                            <Label>Email</Label>
                            <Input
                                data-testid="forgot-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={submitting}
                            data-testid="forgot-submit"
                        >
                            {submitting ? 'Sending code…' : 'Send reset code'}
                        </Button>
                    </form>
                )}

                {step === 'verify' && (
                    <form onSubmit={submitVerify} className="space-y-4" data-testid="forgot-verify-form">
                        <div>
                            <Label>6-digit code</Label>
                            <Input
                                data-testid="forgot-otp"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="[0-9]*"
                                maxLength={6}
                                required
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                className="text-center text-2xl tracking-[0.5em] font-mono"
                                autoFocus
                            />
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-muted-foreground">
                                    {otpTtl > 0 ? (
                                        <>Code expires in <b>{Math.floor(otpTtl / 60)}:{String(otpTtl % 60).padStart(2, '0')}</b></>
                                    ) : (
                                        <span className="text-[hsl(var(--warning-fg))]">Code expired — request a new one</span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                                    onClick={resendCode}
                                    disabled={resendCooldown > 0 || submitting}
                                    data-testid="forgot-resend"
                                >
                                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label>New password</Label>
                            <div className="relative">
                                <Input
                                    data-testid="forgot-new-password"
                                    type={showPw ? 'text' : 'password'}
                                    required
                                    minLength={8}
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    placeholder="At least 8 characters"
                                />
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={showPw ? 'Hide password' : 'Show password'}
                                    tabIndex={-1}
                                >
                                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={submitting || otpTtl === 0}
                            data-testid="forgot-verify-submit"
                        >
                            {submitting ? 'Setting new password…' : 'Reset password & sign in'}
                        </Button>
                        <button
                            type="button"
                            className="w-full text-xs text-muted-foreground hover:underline text-center"
                            onClick={() => { setStep('email'); setOtp(''); setPw(''); }}
                            data-testid="forgot-change-email"
                        >
                            Use a different email
                        </button>
                    </form>
                )}

                <div className="mt-4 text-center">
                    <a href="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={14} /> Back to log in
                    </a>
                </div>
            </Card>
        </AuthShell>
    );
}

/* --------------------------------------------------------------
 * ResetWorkspacePage - nuclear option, wipes everything with recovery code
 * -------------------------------------------------------------- */
export function ResetWorkspacePage() {
    const nav = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [done, setDone] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!confirmed) {
            toast.error('Please confirm you understand this will delete everything');
            return;
        }
        setSubmitting(true);
        try {
            await api.resetWorkspace(email.trim().toLowerCase(), code.trim());
            setDone(true);
            toast.success('Workspace wiped. You can register a new account.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Reset failed');
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <AuthShell testid="reset-workspace-done">
                <Card className="p-6 border-border/80">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success-fg))]" />
                        <h1 className="font-heading text-xl">Workspace wiped</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        All data has been deleted. You can now register a new owner account.
                    </p>
                    <Button className="w-full" onClick={() => nav('/login', { replace: true })} data-testid="reset-workspace-continue">
                        Go to sign up
                    </Button>
                </Card>
            </AuthShell>
        );
    }

    return (
        <AuthShell testid="reset-workspace-page">
            <Card className="p-6 border-border/80">
                <h1 className="font-heading text-xl mb-3">Reset workspace</h1>
                <Alert className="mb-4 border-[hsl(var(--price-increase-border))] bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))]">
                    <Info className="h-4 w-4" />
                    <AlertTitle className="font-heading">This will delete EVERYTHING</AlertTitle>
                    <AlertDescription>
                        All bills, alerts, settings, and the current user account will be permanently removed. This
                        cannot be undone. Only use this if you no longer have password or you want a completely fresh
                        start. Requires the correct recovery code of the currently registered account.
                    </AlertDescription>
                </Alert>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label>Currently registered email</Label>
                        <Input
                            data-testid="reset-workspace-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter the email that is registered"
                        />
                    </div>
                    <div>
                        <Label>Recovery code</Label>
                        <Input
                            data-testid="reset-workspace-code"
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                        />
                    </div>
                    <label className="flex items-start gap-2 text-sm text-muted-foreground">
                        <input
                            type="checkbox"
                            data-testid="reset-workspace-confirm"
                            className="mt-1"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                        />
                        <span>I understand this will permanently delete all my data.</span>
                    </label>
                    <Button
                        type="submit"
                        variant="destructive"
                        className="w-full"
                        disabled={submitting || !confirmed}
                        data-testid="reset-workspace-submit"
                    >
                        {submitting ? 'Resetting…' : 'Wipe workspace'}
                    </Button>
                </form>
                <div className="mt-4 text-center">
                    <a href="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={14} /> Back to log in
                    </a>
                </div>
            </Card>
        </AuthShell>
    );
}

/* --------------------------------------------------------------
 * ResetPasswordPage
 * -------------------------------------------------------------- */
export function ResetPasswordPage() {
    const [sp] = useSearchParams();
    const tokenFromUrl = sp.get('token') || '';
    const [mode, setMode] = useState(tokenFromUrl ? 'token' : 'code');

    const [token, setToken] = useState(tokenFromUrl);
    const [password1, setPassword1] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newRecovery, setNewRecovery] = useState(null);

    useEffect(() => {
        setMode(tokenFromUrl ? 'token' : 'code');
    }, [tokenFromUrl]);

    async function submitToken(e) {
        e.preventDefault();
        if (password1.length < 8) return toast.error('Password must be at least 8 characters');
        setSubmitting(true);
        try {
            await api.resetWithToken(token, password1);
            toast.success('Password reset. Please log in.');
            window.location.href = '/login';
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to reset password');
        } finally {
            setSubmitting(false);
        }
    }

    async function submitCode(e) {
        e.preventDefault();
        if (password1.length < 8) return toast.error('Password must be at least 8 characters');
        setSubmitting(true);
        try {
            const r = await api.resetWithCode(email.trim().toLowerCase(), code.trim(), password1);
            toast.success('Password reset');
            setNewRecovery(r.new_recovery_code);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Invalid email or recovery code');
        } finally {
            setSubmitting(false);
        }
    }

    if (newRecovery) {
        return (
            <AuthShell testid="reset-recovery-shown">
                <Card className="p-6 border-border/80">
                    <h1 className="font-heading text-xl mb-3">New recovery code</h1>
                    <Alert className="mb-4 border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))]">
                        <AlertTitle className="font-heading">Save this new code</AlertTitle>
                        <AlertDescription>Your old recovery code is no longer valid.</AlertDescription>
                    </Alert>
                    <div className="font-mono tabular-nums p-3 rounded-md bg-muted border border-border break-all text-center select-all">
                        {newRecovery}
                    </div>
                    <Button className="mt-4 w-full" onClick={() => (window.location.href = '/login')}>
                        Go to log in
                    </Button>
                </Card>
            </AuthShell>
        );
    }

    return (
        <AuthShell testid="reset-page">
            <Card className="p-6 border-border/80">
                <h1 className="font-heading text-xl mb-3">Reset your password</h1>
                <div className="flex gap-2 mb-4">
                    <button
                        type="button"
                        onClick={() => setMode('code')}
                        data-testid="reset-mode-code"
                        className={
                            'px-3 py-1 rounded-md text-sm border ' +
                            (mode === 'code'
                                ? 'bg-accent border-primary'
                                : 'bg-card border-border text-muted-foreground')
                        }
                    >
                        Recovery code
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('token')}
                        data-testid="reset-mode-token"
                        className={
                            'px-3 py-1 rounded-md text-sm border ' +
                            (mode === 'token'
                                ? 'bg-accent border-primary'
                                : 'bg-card border-border text-muted-foreground')
                        }
                    >
                        Email link
                    </button>
                </div>

                {mode === 'code' ? (
                    <form onSubmit={submitCode} className="space-y-3" data-testid="reset-form-code">
                        <div>
                            <Label>Email</Label>
                            <Input
                                data-testid="reset-code-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>Recovery code</Label>
                            <Input
                                data-testid="reset-code-value"
                                required
                                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>New password</Label>
                            <PasswordInput
                                data-testid="reset-code-newpass"
                                required
                                minLength={8}
                                value={password1}
                                onChange={(e) => setPassword1(e.target.value)}
                            />
                        </div>
                        <Button className="w-full" type="submit" disabled={submitting} data-testid="reset-code-submit">
                            {submitting ? 'Resetting…' : 'Reset password'}
                        </Button>
                    </form>
                ) : (
                    <form onSubmit={submitToken} className="space-y-3" data-testid="reset-form-token">
                        <div>
                            <Label>Reset token (from your email)</Label>
                            <Input
                                data-testid="reset-token-value"
                                required
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>New password</Label>
                            <PasswordInput
                                data-testid="reset-token-newpass"
                                required
                                minLength={8}
                                value={password1}
                                onChange={(e) => setPassword1(e.target.value)}
                            />
                        </div>
                        <Button className="w-full" type="submit" disabled={submitting} data-testid="reset-token-submit">
                            {submitting ? 'Resetting…' : 'Reset password'}
                        </Button>
                    </form>
                )}
                <div className="mt-4 text-center">
                    <a href="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={14} /> Back to log in
                    </a>
                </div>
            </Card>
        </AuthShell>
    );
}
